// frontend/src/api/baseballApi.js
// Mythic Baseball API client — calls /api/baseball/* endpoints.
// All functions return parsed JSON. Errors thrown loudly — no silent failures.
// Phase 3E — Sub-unit 3E.1

import { API_BASE, getCsrfHeaders } from './transport';

const BASEBALL_BASE = `${API_BASE}/baseball`;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Parse a baseball API response. Throws loudly on non-2xx.
 * Logs _stub: true responses as a developer hint (but does NOT throw).
 */
async function parseBaseballResponse(response, context) {
    if (!response.ok) {
        let errorMsg = `HTTP ${response.status}`;
        try {
            const errorData = await response.json();
            errorMsg = errorData.error || errorData.message || errorData.detail || errorMsg;
        } catch (_) {
            try {
                const rawText = await response.text();
                if (rawText) {
                    errorMsg = `${errorMsg}: ${rawText}`;
                }
            } catch (__) {
                // cannot read body — use status only
            }
        }
        throw new Error(`❌ baseballApi [${context}]: ${errorMsg}`);
    }

    const data = await response.json();

    if (data && data._stub === true) {
        console.warn(`⚠️ baseballApi [${context}]: Received stub response (_stub: true) — real endpoint not yet active.`);
    }

    return data;
}

// ---------------------------------------------------------------------------
// League APIs
// ---------------------------------------------------------------------------

/**
 * GET /api/baseball/leagues/
 * Returns: { leagues: [...], active_window: {...} }
 */
export function getBaseballLeagues() {
    return fetch(`${BASEBALL_BASE}/leagues/`, {
        method: 'GET',
        credentials: 'include',
    }).then(response => parseBaseballResponse(response, 'getBaseballLeagues'));
}

/**
 * GET /api/baseball/leagues/<leagueId>/
 * Returns: league detail with teams array, standings, active_window, status
 * @param {number|string} leagueId
 */
export function getBaseballLeague(leagueId) {
    if (!leagueId) {
        throw new Error('❌ baseballApi [getBaseballLeague]: leagueId is required');
    }
    return fetch(`${BASEBALL_BASE}/leagues/${leagueId}/`, {
        method: 'GET',
        credentials: 'include',
    }).then(response => parseBaseballResponse(response, 'getBaseballLeague'));
}

/**
 * POST /api/baseball/leagues/<leagueId>/join/
 * Creates a BaseballTeam for the authenticated user in this league.
 * @param {number|string} leagueId
 */
export function joinBaseballLeague(leagueId) {
    if (!leagueId) {
        throw new Error('❌ baseballApi [joinBaseballLeague]: leagueId is required');
    }
    return fetch(`${BASEBALL_BASE}/leagues/${leagueId}/join/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({}),
    }).then(response => parseBaseballResponse(response, 'joinBaseballLeague'));
}

// ---------------------------------------------------------------------------
// Lineup APIs
// ---------------------------------------------------------------------------

/**
 * GET /api/baseball/lineup/?team_id=<teamId>&contest_period_id=<contestPeriodId>
 * Returns: 9-slot lineup with player details and current window scores.
 * @param {number|string} teamId
 * @param {number|string} contestPeriodId
 */
export function getBaseballLineup(teamId, contestPeriodId) {
    if (!teamId) {
        throw new Error('❌ baseballApi [getBaseballLineup]: teamId is required');
    }
    if (!contestPeriodId) {
        throw new Error('❌ baseballApi [getBaseballLineup]: contestPeriodId is required');
    }
    const params = new URLSearchParams({ team_id: teamId, contest_period_id: contestPeriodId });
    return fetch(`${BASEBALL_BASE}/lineup/?${params}`, {
        method: 'GET',
        credentials: 'include',
    }).then(response => parseBaseballResponse(response, 'getBaseballLineup'));
}

/**
 * POST /api/baseball/lineup/update/
 * Body: { team_id, contest_period_id, slots: [{slot_type, player_id}] }
 * Returns: updated lineup with is_complete, is_locked status.
 * @param {number|string} teamId
 * @param {number|string} contestPeriodId
 * @param {{ slots: Array<{slot_type: string, player_id: string|number}> }} lineupData
 */
export function updateBaseballLineup(teamId, contestPeriodId, lineupData) {
    if (!teamId) {
        throw new Error('❌ baseballApi [updateBaseballLineup]: teamId is required');
    }
    if (!contestPeriodId) {
        throw new Error('❌ baseballApi [updateBaseballLineup]: contestPeriodId is required');
    }
    if (!lineupData || !Array.isArray(lineupData.slots)) {
        throw new Error('❌ baseballApi [updateBaseballLineup]: lineupData.slots (array) is required');
    }
    return fetch(`${BASEBALL_BASE}/lineup/update/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({
            team_id: teamId,
            contest_period_id: contestPeriodId,
            slots: lineupData.slots,
        }),
    }).then(response => parseBaseballResponse(response, 'updateBaseballLineup'));
}

/**
 * POST /api/baseball/lineup/lock/
 * Body: { team_id, contest_period_id }
 * Returns: { success, locked_at, lineup_id }
 * @param {number|string} teamId
 * @param {number|string} contestPeriodId
 */
export function lockBaseballLineup(teamId, contestPeriodId) {
    if (!teamId) {
        throw new Error('❌ baseballApi [lockBaseballLineup]: teamId is required');
    }
    if (!contestPeriodId) {
        throw new Error('❌ baseballApi [lockBaseballLineup]: contestPeriodId is required');
    }
    return fetch(`${BASEBALL_BASE}/lineup/lock/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({ team_id: teamId, contest_period_id: contestPeriodId }),
    }).then(response => parseBaseballResponse(response, 'lockBaseballLineup'));
}

// ---------------------------------------------------------------------------
// Matchup APIs
// ---------------------------------------------------------------------------

/**
 * GET /api/baseball/leagues/<leagueId>/matchups/
 * Returns: { matchups: [...] } for the current window in this league.
 * @param {number|string} leagueId
 */
export function getBaseballMatchups(leagueId) {
    if (!leagueId) {
        throw new Error('❌ baseballApi [getBaseballMatchups]: leagueId is required');
    }
    return fetch(`${BASEBALL_BASE}/leagues/${leagueId}/matchups/`, {
        method: 'GET',
        credentials: 'include',
    }).then(response => parseBaseballResponse(response, 'getBaseballMatchups'));
}

/**
 * GET /api/baseball/matchup/<matchupId>/
 * Returns: matchup with both teams' scores and per-player breakdown.
 * @param {number|string} matchupId
 */
export function getBaseballMatchup(matchupId) {
    if (!matchupId) {
        throw new Error('❌ baseballApi [getBaseballMatchup]: matchupId is required');
    }
    return fetch(`${BASEBALL_BASE}/matchup/${matchupId}/`, {
        method: 'GET',
        credentials: 'include',
    }).then(response => parseBaseballResponse(response, 'getBaseballMatchup'));
}

// ---------------------------------------------------------------------------
// Player APIs
// ---------------------------------------------------------------------------

/**
 * GET /api/baseball/players/?position=<positionGroup>&rarity=<rarityFilter>&active=true
 * Optional: &contest_period_id=<id> to include games_this_window count.
 * Optional: &team_id=<id> to return only players owned by that team.
 * Returns: { count, players: [{player_id, name, position, team, rarity_tier, games_this_window}] }
 * @param {string|null} positionGroup  — e.g. 'INFIELD', 'OUTFIELD', 'FIRST_BASE', 'CATCHER', 'DH'
 * @param {string|null} rarityFilter   — e.g. 'LEGENDARY', 'RARE', 'COMMON'
 * @param {number|string|null} contestPeriodId
 * @param {number|string|null} teamId  — if provided, returns only players on that team
 */
export function getBaseballPlayers(positionGroup = null, rarityFilter = null, contestPeriodId = null, teamId = null) {
    const params = new URLSearchParams({ active: 'true' });
    if (positionGroup) params.append('position', positionGroup);
    if (rarityFilter) params.append('rarity', rarityFilter);
    if (contestPeriodId) params.append('contest_period_id', contestPeriodId);
    if (teamId) params.append('team_id', String(teamId));

    return fetch(`${BASEBALL_BASE}/players/?${params}`, {
        method: 'GET',
        credentials: 'include',
    }).then(response => parseBaseballResponse(response, 'getBaseballPlayers'));
}

// ---------------------------------------------------------------------------
// Contest Period APIs
// ---------------------------------------------------------------------------

/**
 * GET /api/baseball/contest-periods/?status=<status>
 * Returns: array of WINDOW ContestPeriod records.
 * @param {string|null} status — e.g. 'UPCOMING', 'ACTIVE', 'COMPLETE'
 */
export function getBaseballContestPeriods(status = null) {
    const params = new URLSearchParams();
    if (status) params.append('status', status);

    const query = params.toString() ? `?${params}` : '';
    return fetch(`${BASEBALL_BASE}/contest-periods/${query}`, {
        method: 'GET',
        credentials: 'include',
    }).then(response => parseBaseballResponse(response, 'getBaseballContestPeriods'));
}

// ---------------------------------------------------------------------------
// League Creation API (Phase 3D)
// ---------------------------------------------------------------------------

/**
 * POST /api/baseball/leagues/create/
 * Creates a new BaseballLeague in REGISTRATION status.
 * @param {string} name        — REQUIRED: league name (non-empty)
 * @param {string} leagueType  — 'PUBLIC' or 'PRIVATE' (default: 'PUBLIC')
 * Returns: { status: 'created', league_id, league_name, league_type, contest_period_id }
 */
export function createBaseballLeague(name, leagueType = 'PUBLIC') {
    if (!name || typeof name !== 'string' || !name.trim()) {
        throw new Error('❌ baseballApi [createBaseballLeague]: name is required and must be a non-empty string');
    }
    const validTypes = ['PUBLIC', 'PRIVATE'];
    const normalizedType = leagueType ? leagueType.toUpperCase() : 'PUBLIC';
    if (!validTypes.includes(normalizedType)) {
        throw new Error(
            `❌ baseballApi [createBaseballLeague]: leagueType must be PUBLIC or PRIVATE. Got: ${JSON.stringify(leagueType)}`
        );
    }
    return fetch(`${BASEBALL_BASE}/leagues/create/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim(), league_type: normalizedType }),
    }).then(response => parseBaseballResponse(response, 'createBaseballLeague'));
}

// ---------------------------------------------------------------------------
// Gear APIs (Phase 3D)
// ---------------------------------------------------------------------------

/**
 * GET /api/baseball/gear/?team_id=<teamId>
 * Returns MLB GearCatalog items with equipped state for the given team.
 * @param {number|string} teamId — REQUIRED
 * Returns: { gear: [{gear_id, item_id, name, description, sport_scope, rarity, status, is_equipped, equipped_slot}], count }
 */
export function getBaseballGear(teamId) {
    if (!teamId) {
        throw new Error('❌ baseballApi [getBaseballGear]: teamId is required');
    }
    const params = new URLSearchParams({ team_id: teamId });
    return fetch(`${BASEBALL_BASE}/gear/?${params}`, {
        method: 'GET',
        credentials: 'include',
    }).then(response => parseBaseballResponse(response, 'getBaseballGear'));
}

/**
 * POST /api/baseball/gear/equip/
 * Equips a gear item to a lineup slot.
 * @param {number|string} teamId           — REQUIRED
 * @param {number|string} contestPeriodId  — REQUIRED
 * @param {string}        slotType         — REQUIRED: one of the 9 slot type strings
 * @param {number|string} gearId           — REQUIRED: GearCatalog pk
 * Returns: { status: 'equipped', gear_id, gear_name, slot_type, team_id }
 */
export function equipBaseballGear(teamId, contestPeriodId, slotType, gearId) {
    if (!teamId) throw new Error('❌ baseballApi [equipBaseballGear]: teamId is required');
    if (!contestPeriodId) throw new Error('❌ baseballApi [equipBaseballGear]: contestPeriodId is required');
    if (!slotType) throw new Error('❌ baseballApi [equipBaseballGear]: slotType is required');
    if (gearId === undefined || gearId === null) throw new Error('❌ baseballApi [equipBaseballGear]: gearId is required');
    return fetch(`${BASEBALL_BASE}/gear/equip/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({
            team_id: teamId,
            contest_period_id: contestPeriodId,
            slot_type: slotType,
            gear_id: gearId,
        }),
    }).then(response => parseBaseballResponse(response, 'equipBaseballGear'));
}

/**
 * POST /api/baseball/gear/unequip/
 * Removes gear from a lineup slot. Idempotent — no error if nothing was equipped.
 * @param {number|string} teamId           — REQUIRED
 * @param {number|string} contestPeriodId  — REQUIRED
 * @param {string}        slotType         — REQUIRED: one of the 9 slot type strings
 * Returns: { status: 'unequipped' | 'nothing_equipped', slot_type, team_id }
 */
export function unequipBaseballGear(teamId, contestPeriodId, slotType) {
    if (!teamId) throw new Error('❌ baseballApi [unequipBaseballGear]: teamId is required');
    if (!contestPeriodId) throw new Error('❌ baseballApi [unequipBaseballGear]: contestPeriodId is required');
    if (!slotType) throw new Error('❌ baseballApi [unequipBaseballGear]: slotType is required');
    return fetch(`${BASEBALL_BASE}/gear/unequip/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({
            team_id: teamId,
            contest_period_id: contestPeriodId,
            slot_type: slotType,
        }),
    }).then(response => parseBaseballResponse(response, 'unequipBaseballGear'));
}

// ---------------------------------------------------------------------------
// Pack APIs
// ---------------------------------------------------------------------------

const _VALID_RARITIES = ['COMMON', 'RARE', 'LEGENDARY'];

/**
 * POST /api/baseball/packs/pick/
 * Persists a pack card pick to the backend (BaseballTeamPlayer record).
 * @param {number|string} teamId   — REQUIRED
 * @param {string} playerId        — REQUIRED
 * @param {string} slotType        — REQUIRED: e.g. 'INFIELD', 'OUTFIELD', 'DH'
 * @param {string} rarity          — REQUIRED: 'COMMON', 'RARE', or 'LEGENDARY'
 * Returns: { status: 'picked', player_id, player_name, slot, team_id, created }
 */
export async function pickBaseballCard(teamId, playerId, slotType, rarity) {
    if (!teamId) throw new Error('❌ baseballApi [pickBaseballCard]: teamId is required');
    if (!playerId) throw new Error('❌ baseballApi [pickBaseballCard]: playerId is required');
    if (!slotType) throw new Error('❌ baseballApi [pickBaseballCard]: slotType is required');
    if (!rarity) throw new Error('❌ baseballApi [pickBaseballCard]: rarity is required');
    if (!_VALID_RARITIES.includes(rarity)) {
        throw new Error(
            `❌ baseballApi [pickBaseballCard]: invalid rarity=${JSON.stringify(rarity)}. Must be one of: ${_VALID_RARITIES.join(', ')}`
        );
    }
    const response = await fetch(`${BASEBALL_BASE}/packs/pick/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({
            team_id: teamId,
            player_id: playerId,
            slot: slotType,
            rarity: rarity,
        }),
    });
    return parseBaseballResponse(response, 'pickBaseballCard');
}

/**
 * POST /api/baseball/packs/open/
 * @param {string} rarity — REQUIRED: 'COMMON', 'RARE', or 'LEGENDARY'
 * Returns: { status, rarity, cards: [...] }
 */
export function openBaseballPack(rarity) {
    if (!rarity) {
        throw new Error(
            `❌ baseballApi [openBaseballPack]: rarity is required. Must be one of: ${_VALID_RARITIES.join(', ')}`
        );
    }
    if (!_VALID_RARITIES.includes(rarity)) {
        throw new Error(
            `❌ baseballApi [openBaseballPack]: invalid rarity=${JSON.stringify(rarity)}. Must be one of: ${_VALID_RARITIES.join(', ')}`
        );
    }
    return fetch(`${BASEBALL_BASE}/packs/open/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({ rarity }),
    }).then(response => parseBaseballResponse(response, 'openBaseballPack'));
}
